import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import qs from "qs";
import React, { useEffect, useMemo, useReducer } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import CalendarDateFilters from "../../components/ui/Calendar";
import Pagination from "../../components/ui/Pagination";
import RenderTable from "../../components/ui/RenderTable";
import {
  bulkDeleteReducer,
  bulkDeleteState,
} from "../../context/bulkDeleteReducer";
import { RideStatusLabels } from "../../helpers/enums";
import { Chip, pluralize, Query } from "../../helpers/general";
import { useRides } from "../../hooks/useRides";
import { general } from "../../locales/general";
import AdminRoute from "../auth/RestrictedRoute";
import RidersTable from "./RidersTable";
import { cardStyle } from "../Places/PlacesTable";
import { controlStyle } from "../../helpers/constants";

const PAGE_SIZE = 10;

const STATUS_OPTIONS = Object.entries(RideStatusLabels).map(
  ([value, label]) => ({ value, label: String(label) }),
);

const PAYMENT_OPTIONS = [
  { value: "cash", label: "Espèces" },
  { value: "user_balance", label: "Solde utilisateur" },
];

const VALIDITY_OPTIONS = [
  { value: "true", label: "Valide" },
  { value: "false", label: "Non valide" },
  { value: "pending", label: "En attente" },
];

function FilterSelect({
  id,
  label,
  value,
  onChange,
  options,
}: {
  id: string;
  label: string;
  value?: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div className="d-flex flex-column gap-1">
      <label htmlFor={id} className="text-muted mb-0" style={{ fontSize: 11 }}>
        {label}
      </label>
      <select
        id={id}
        className="form-select px-3"
        style={{ ...controlStyle, width: "auto", minWidth: 150 }}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">Tous</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export default function Riders() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const initialQueries: Partial<Query> = useMemo(() => {
    const parsed = qs.parse(searchParams.toString());
    return {
      ...parsed,
      includeHistory: "false",
      page: parsed.page ? Number(parsed.page as string) : undefined,
    };
  }, [searchParams]);

  const { queries, addQuery, isLoading, rides, setDates } =
    useRides(initialQueries);

  const [ridesToBeDeleted, dispatch] = useReducer(
    bulkDeleteReducer,
    bulkDeleteState,
  );

  // Keep the filters in the URL
  useEffect(() => {
    const updated = { ...queries, page: queries.page || 1 };
    navigate(`?${qs.stringify(updated, { skipNulls: true })}`, {
      replace: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(queries), navigate]);

  // Any filter change goes back to page 1
  const setFilter = (key: any, value: any) => {
    addQuery(key, value);
    if (key !== "page") addQuery("page", 1);
  };

  const total = rides?.meta?.totalItems ?? 0;

  return (
    <AdminRoute>
      <section>
        {/* Title + count */}
        <div className="d-flex align-items-center gap-2 mb-3">
          <h5 className="fs-4 fw-bold mb-0">Trajets</h5>
          {rides && (
            <Chip tone="warning">
              {total} {pluralize("Trajet", total)}
            </Chip>
          )}
        </div>

        {/* Filters */}
        <div
          style={{ ...cardStyle, padding: "14px 16px" }}
          className="d-flex flex-wrap align-items-end gap-3 mb-3"
        >
          <CalendarDateFilters
            label="Période"
            onChange={setDates}
            defaultStartDate={queries.startDate as string}
            defaultEndDate={queries.endDate as string}
          />

          <FilterSelect
            id="status"
            label="Statut"
            value={queries.status}
            onChange={(v) => setFilter("status", v || undefined)}
            options={STATUS_OPTIONS}
          />

          <FilterSelect
            id="paymentMethod"
            label="Mode de paiement"
            value={queries.paymentMethod}
            onChange={(v) => setFilter("paymentMethod", v || undefined)}
            options={PAYMENT_OPTIONS}
          />

          <FilterSelect
            id="isValid"
            label="Trajet valide ?"
            value={queries.isValidRide}
            onChange={(v) => setFilter("isValidRide", v || undefined)}
            options={VALIDITY_OPTIONS}
          />

          <div
            className="d-flex align-items-center gap-2 px-3 ms-auto flex-grow-1"
            style={{ ...controlStyle, minWidth: 200, maxWidth: 320 }}
          >
            <MagnifyingGlassIcon color="gray" width={18} />
            <input
              id="query"
              type="text"
              placeholder="Rechercher"
              className="border-0 w-100 bg-transparent"
              style={{ outline: "none", fontSize: 13 }}
              value={queries.query ?? ""}
              onChange={(e) => setFilter("query", e.target.value || undefined)}
            />
          </div>
        </div>

        {/* Table */}
        <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
          <RenderTable
            loading={isLoading}
            render={() =>
              rides?.items && rides.items.length > 0 ? (
                <RidersTable
                  riders={rides.items}
                  onRiderSelect={dispatch}
                  selectedRides={ridesToBeDeleted.ids}
                />
              ) : (
                <p className="text-center text-muted py-5 mb-0">
                  {general.fr.noData}
                </p>
              )
            }
          />
        </div>

        <Pagination
          currentPage={Number(queries.page) || 1}
          onPageChange={(page) => setFilter("page", page)}
          siblingCount={1}
          totalCount={rides?.meta?.totalPages || 0}
          pageSize={PAGE_SIZE}
        />
      </section>
    </AdminRoute>
  );
}
